// The full composer screen.
//
// What is left here is the screen: the controls, in the order they are worked
// through, and the sentences that explain a refusal. Everything it *does* —
// media rules, validation, the plan, the publish pipeline — lives in
// `useComposer`, which the dashboard's quick composer uses as well. That is the
// whole of the refactor: two views, one implementation, so a fix to publishing
// reaches both without being made twice.
//
// The screen is two columns and one bar. The left column is the post being
// made, top to bottom in the order it is made in: who it is for, the words, the
// pictures, where it goes. The right column is everything that only answers a
// question about that post — what it will look like, and the per-network
// settings — which is why none of it is in the writing column interrupting the
// writing. The bar at the foot is the two things that end the session, and it
// is sticky because the decision to publish is taken while looking at the post,
// not after scrolling past it.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ComposerProvider, useSharedComposer, PostPreviewCard, MAX_PHOTOS,
} from "../compose";
import { formatBytes } from "../lib/media";
import { formatInZone } from "../lib/timezone";
import { formatLimit, limitOf } from "../lib/entitlements";
import { useEntitlements } from "../lib/useEntitlements";
import { TargetPicker } from "../components/TargetPicker";
import { MediaPicker } from "../components/MediaPicker";
import { DraftPicker } from "../components/DraftPicker";
import { ContentPolicyNotice } from "../components/ContentPolicyNotice";
import { Spinner } from "../components/Spinner";
import { groupedTemplates } from "../lib/captionTemplates";
import { MAX_CAPTION_CHARS } from "../lib/sheet";
import type { YouTubePrivacy } from "../lib/types";

/** The writing panel, or the templates that fill it. */
type WriterTab = "write" | "templates";
/** The right-hand column: what it will look like, or how it goes out. */
type SideTab = "preview" | "settings";

export function Compose() {
  // One composer, read by both columns. The preview card is written against the
  // shared instance (it is the dashboard's preview), so the instance is made
  // above both columns rather than inside the form and threaded across.
  return (
    <ComposerProvider>
      <ComposeScreen />
    </ComposerProvider>
  );
}

function ComposeScreen() {
  const c = useSharedComposer();
  // Two dialogs, and nothing else: picking a file and picking a saved caption
  // are things this screen does, not things a post has.
  const [picking, setPicking] = useState(false);
  const [pickingDraft, setPickingDraft] = useState(false);
  const [writerTab, setWriterTab] = useState<WriterTab>("write");
  const [sideTab, setSideTab] = useState<SideTab>("preview");
  const fileInput = useRef<HTMLInputElement>(null);
  // Only for the one sentence that quotes the plan's horizon in days.
  const { entitlements } = useEntitlements(c.workspaceId);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])];
    // Cleared here rather than after the await: the same file chosen twice in a
    // row fires no change event otherwise, and re-picking a photo you just
    // removed is the most ordinary thing anyone does on this screen.
    if (fileInput.current) fileInput.current.value = "";
    await c.addFiles(files);
  }

  function chooseBrand(brandId: string | null) {
    // The brand menu describes the post, not the view: changing it is also a
    // publishing boundary, so clear first — accounts from the brand that was
    // being looked at must never stay selected but hidden.
    c.setTargets(c.activeConnections.map((connection) => connection.id), false);
    if (brandId) {
      c.setTargets(
        c.activeConnections
          .filter((connection) => connection.brand_id === brandId)
          .map((connection) => connection.id),
        true,
      );
    }
    c.selectBrand(brandId);
  }

  const publishConnections = c.activeBrandId
    ? c.activeConnections.filter((connection) => connection.brand_id === c.activeBrandId)
    : c.activeConnections;

  const needsYouTubeTitle =
    c.kind === "video" && c.selectedPlatforms.includes("youtube") && !c.title;
  const canPublish =
    (!c.whatsappFlow || (c.whatsappReady && !!c.activeBrandId && c.kind === "photo")) &&
    !c.busy && !!c.facts && c.selected.size > 0 && c.allReady && !c.videoBlocked &&
    c.postsLeft !== 0 && !c.blockedByRole && !needsYouTubeTitle;

  return (
    <div className="compose-page">
      <header className="compose-hero">
        <div className="compose-hero-text">
          <h1>Compose for <span>all your brands</span></h1>
          <p>Create once, publish everywhere. Save time and grow faster.</p>
        </div>
        <div className="compose-hero-mark" aria-hidden="true">
          <small>One post.</small>
          <strong>Many platforms.</strong>
          <span>↗</span>
        </div>
      </header>

      <div className="compose-grid">
        {/* ------------------------------------------------ the post itself */}
        <div className="compose-main">
          <div className="card compose-create-card">
            <div className="compose-section-head">
              <div className="compose-section-title">
                <h2>Create your post{c.brands.length > 0 && " for"}</h2>
                {c.brands.length > 0 && (
                  <label className="compose-brand-field">
                    <span className="visually-hidden">Create your post for</span>
                    <select
                      aria-label="Create your post for"
                      value={c.activeBrandId ?? ""}
                      onChange={(e) => chooseBrand(e.target.value || null)}
                    >
                      <option value="">All brands</option>
                      {c.brands.map((brand) => (
                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <p className="muted">
                Write the message, add your media, then choose where it goes.
              </p>
            </div>

            {/* Three ways to fill the box, in one row, because from the
                writer's side they are one decision: type it, start from a
                template, or bring one you saved. Only the first two are panels
                — a saved draft is a library, and a library is a dialog. */}
            <div className="compose-tabs" role="tablist" aria-label="How to write this post">
              <button type="button" role="tab" aria-selected={writerTab === "write"}
                      className={writerTab === "write" ? "compose-tab on" : "compose-tab"}
                      onClick={() => setWriterTab("write")}>
                <span className="compose-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </span>
                Write
              </button>
              <button type="button" role="tab" aria-selected={writerTab === "templates"}
                      className={writerTab === "templates" ? "compose-tab on" : "compose-tab"}
                      disabled={c.busy}
                      onClick={() => setWriterTab("templates")}>
                <span className="compose-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16v6H4zM4 14h7v6H4zM15 14h5v6h-5z" />
                  </svg>
                </span>
                Templates
              </button>
              <button type="button" className="compose-tab" disabled={c.busy}
                      onClick={() => setPickingDraft(true)}>
                <span className="compose-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 7h6l2 2h10v10H3zM3 7V5h6l2 2" />
                  </svg>
                </span>
                Saved drafts
              </button>
            </div>

            {writerTab === "templates" ? (
              <div className="compose-templates">
                <p className="muted">
                  Pick one and it fills the box. The square brackets are yours to write over;
                  the <code>{"{{"}braces{"}}"}</code> fill themselves in from this brand's details.
                </p>
                {groupedTemplates().map(([group, templates]) => (
                  <div key={group} className="compose-template-group">
                    <h4>{group}</h4>
                    <div className="compose-template-row">
                      {templates.map((t) => (
                        <button key={t.key} type="button" className="compose-template"
                                disabled={c.busy}
                                onClick={() => { c.applyTemplate(t.key); setWriterTab("write"); }}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="compose-writer">
                  <label className="visually-hidden" htmlFor="caption">Caption</label>
                  <textarea id="caption" value={c.caption} disabled={c.busy}
                            placeholder="What would you like to share?"
                            onChange={(e) => c.setCaption(e.target.value)} />
                  <div className="compose-writer-foot">
                    <button type="button" className="compose-writer-action"
                            onClick={() => setWriterTab("templates")} disabled={c.busy}>
                      Start from a template
                    </button>
                    <button type="button"
                            className={c.savingDraft
                              ? "compose-writer-action is-busy" : "compose-writer-action"}
                            onClick={() => void c.saveAsDraft()}
                            disabled={c.busy || c.savingDraft || !c.caption.trim()}>
                      {c.savingDraft && <Spinner />}
                      Save to library
                    </button>
                    <span className="spacer" />
                    <span className={c.caption.length > MAX_CAPTION_CHARS
                      ? "compose-count over" : "compose-count"}>
                      {c.caption.length.toLocaleString()} / {MAX_CAPTION_CHARS.toLocaleString()}
                    </span>
                  </div>
                </div>

                {c.unresolved.length > 0 && (
                  <p className="notice caption-tokens">
                    This caption still says {c.unresolved.map((k) => `{{${k}}}`).join(", ")}. It
                    publishes exactly like that unless you{" "}
                    <button type="button" className="linkish" onClick={c.fillTokens}>
                      fill in the brand details
                    </button>
                    {c.detailValues.size === 0 && <> — there are none set for this brand yet</>}.
                  </p>
                )}
                {c.captionUndo && (
                  <p className="muted caption-undo">
                    {c.captionUndo.from === "template" ? "A template" : "A saved draft"} replaced
                    what you had written.{" "}
                    <button type="button" className="linkish" onClick={c.undoCaption}>
                      Put it back
                    </button>
                  </p>
                )}
                {/* The only mention of the library on this screen, and
                    deliberately a link rather than a control: Compose selects,
                    and everything that fills the library — importing a file,
                    deleting, tidying up — lives on one screen away from a post
                    that is about to go out. */}
                <p className="muted caption-library-hint">
                  Written your captions somewhere else? Bring a spreadsheet or a markdown file
                  into your <Link to="/captions">caption library</Link> and they appear under
                  “Saved drafts”.
                </p>
              </>
            )}

            {/* ------------------------------------------------------ media */}
            <div className="compose-block">
              <ContentPolicyNotice where="compose" />
              {/* The native file input is the thing that actually opens the file
                  dialog, but it renders as "Choose File / no file chosen", which
                  nobody reads as an upload button. Keep it, hide it, and drive it
                  from a button that looks like the one beside it. */}
              {/* `multiple` is the whole of "add several photos at once" — the
                  dialog already knows how, it was only ever asked for one. */}
              <input ref={fileInput} type="file" accept="image/*,video/*" multiple onChange={pick}
                     className="visually-hidden" tabIndex={-1} />
              <div className="compose-drop">
                <p className="compose-drop-head">
                  <span className="media-source-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6" />
                      <circle cx="8.5" cy="9" r="1.5" />
                    </svg>
                  </span>
                  <strong>Add photos or videos</strong>
                </p>
                <p className="muted">Choose files from your device or your media library.</p>
                <div className="row media-source">
                  <button className="media-source-button upload"
                          onClick={() => fileInput.current?.click()}
                          disabled={!c.mayUpload || c.busy || !c.canAddMore}>
                    <span className="media-source-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
                      </svg>
                    </span>
                    <span>
                    {c.items.length === 0 ? "Upload from this device"
                      : c.isVideo ? "Replace from this device"
                      : "Add more from this device"}
                    </span>
                  </button>
                  <button className="media-source-button library" onClick={() => setPicking(true)}
                          disabled={!c.workspaceId || c.busy || !c.canAddMore}>
                    <span className="media-source-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                           strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7h7l2 2h9v10H3zM3 7V5h7l2 2" />
                      </svg>
                    </span>
                    <span>
                    {c.items.length === 0 ? "Choose from your media"
                      : c.isVideo ? "Replace from your media"
                      : "Add more from your media"}
                    </span>
                  </button>
                </div>
                {c.items.length === 0 && c.mayUpload && (
                  <p className="muted compose-drop-hint">
                    Pick up to {MAX_PHOTOS} photos and they go out as one post — a carousel on
                    Instagram, a multi-photo post on Facebook. Want them stored before you write?{" "}
                    <Link to="/media">Your media library</Link> takes many files at once.
                  </p>
                )}
                {!c.mayUpload && (
                  <p className="muted compose-drop-hint">
                    You can publish what is already in the library, but uploading new files is a
                    role your account does not have.
                  </p>
                )}
              </div>

              {c.items.length > 0 && (
                <>
                  <p className="muted media-count">
                    {c.isVideo
                      ? "One video — videos go out on their own."
                      : c.items.length === 1
                        ? `1 photo. Add up to ${MAX_PHOTOS - 1} more and they publish as one post.`
                        : `${c.items.length} photos, in this order. ` +
                          (c.slotsLeft > 0 ? `Room for ${c.slotsLeft} more.` : "That is the limit.")}
                  </p>

                  {/* Ordered, numbered and re-orderable, because on a carousel the
                      order is not a display preference — it is what gets published. */}
                  <ol className="compose-media">
                    {c.items.map((it, i) => (
                      <li key={it.key} className="compose-slide">
                        <span className="thumb">
                          {it.previewUrl
                            // A file chosen from the device is still a blob URL and
                            // still a real video; a file chosen from the library now
                            // arrives as a poster frame, which is a picture
                            // (0017-v2 §2).
                            ? it.facts.mimeType.startsWith("video/")
                              && !(it.asset && it.asset.preview_path)
                              ? <video src={it.previewUrl} preload="metadata" muted playsInline />
                              : <img src={it.previewUrl} alt={`Photo ${i + 1}`} />
                            : <span className="thumb-fallback">🖼</span>}
                          {!c.isVideo && c.items.length > 1 && (
                            <span className="pick-badge">{i + 1}</span>
                          )}
                        </span>

                        <span className="slide-facts muted">
                          <strong>{it.file?.name ?? it.asset?.original_filename ?? "Untitled"}</strong>
                          <br />
                          {it.facts.mimeType} · {formatBytes(it.facts.byteSize)}
                          {it.facts.width ? ` · ${it.facts.width}×${it.facts.height}` : ""}
                          {it.facts.durationSeconds ? ` · ${it.facts.durationSeconds.toFixed(1)}s` : ""}
                          <br />
                          {it.asset ? "Already in your library" : "Uploads when you publish"}
                        </span>

                        <span className="slide-actions">
                          {c.items.length > 1 && (
                            <>
                              <button type="button" disabled={c.busy || i === 0}
                                      aria-label={`Move photo ${i + 1} earlier`}
                                      onClick={() => c.moveItem(it.key, -1)}>↑</button>
                              <button type="button" disabled={c.busy || i === c.items.length - 1}
                                      aria-label={`Move photo ${i + 1} later`}
                                      onClick={() => c.moveItem(it.key, 1)}>↓</button>
                            </>
                          )}
                          <button type="button" disabled={c.busy}
                                  aria-label={`Remove ${it.file?.name ?? it.asset?.original_filename ?? `photo ${i + 1}`}`}
                                  onClick={() => c.removeItem(it.key)}>Remove</button>
                        </span>
                      </li>
                    ))}
                  </ol>

                  {c.items.some((it) => it.asset) && (
                    <p className="muted compose-drop-hint">
                      Files from your library are not uploaded again.{" "}
                      <Link to="/media">See all media</Link>
                    </p>
                  )}
                </>
              )}

              {c.videoBlocked && (
                <div className="notice upgrade" style={{ marginTop: ".8rem" }}>
                  Video publishing is not included in your plan.{" "}
                  <Link to="/plans">See plans</Link> — or post a photo instead.
                </div>
              )}
            </div>

            {/* -------------------------------------------------- publish to */}
            <div className="compose-block compose-publish-block">
              <div className="compose-block-head">
                <h3>Publish to</h3>
                <p className="muted">
                  Select the connected accounts that should receive this post.
                </p>
              </div>
              <TargetPicker
                connections={publishConnections}
                selected={c.selected}
                results={c.results}
                checking={c.checking}
                onToggle={c.toggleTarget}
                onToggleMany={c.setTargets}
                onFix={c.applyFix}
                brands={c.brands}
                activeBrandId={c.activeBrandId}
              />
            </div>
          </div>

          {c.notice && <div className="card"><div className="notice">{c.notice}</div></div>}
          {c.paywall && (
            <div className="card">
              <div className="notice upgrade">
                {c.paywall} <Link to="/plans">See plans</Link>
              </div>
            </div>
          )}
          {c.error && <div className="card err">{c.error}</div>}
        </div>

        {/* ------------------------------------- what it looks like, and how */}
        <aside className="compose-side">
          <div className="card compose-side-card">
            <div className="compose-tabs side" role="tablist" aria-label="About this post">
              <button type="button" role="tab" aria-selected={sideTab === "preview"}
                      className={sideTab === "preview" ? "compose-tab on" : "compose-tab"}
                      onClick={() => setSideTab("preview")}>
                <span className="compose-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </span>
                Preview
              </button>
              <button type="button" role="tab" aria-selected={sideTab === "settings"}
                      className={sideTab === "settings" ? "compose-tab on" : "compose-tab"}
                      onClick={() => setSideTab("settings")}>
                <span className="compose-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
                  </svg>
                </span>
                Settings
              </button>
            </div>

            {sideTab === "preview" ? (
              <PostPreviewCard bare />
            ) : (
              <div className="compose-settings">
                {c.kind === "video" && c.selectedPlatforms.includes("youtube") && (
                  <>
                    <label htmlFor="title">YouTube title</label>
                    <input id="title" value={c.title} onChange={(e) => c.setTitle(e.target.value)}
                           placeholder="Required for YouTube" />
                    <div style={{ height: ".7rem" }} />

                    <label htmlFor="yt-privacy">Who can see this on YouTube</label>
                    <select id="yt-privacy" value={c.ytPrivacy}
                            onChange={(e) => c.setYtPrivacy(e.target.value as YouTubePrivacy)}>
                      <option value="public">Public — anyone can find and watch it</option>
                      <option value="unlisted">Unlisted — only people with the link</option>
                      <option value="private">Private — only you</option>
                    </select>
                    <div style={{ height: ".7rem" }} />
                  </>
                )}

                {/* The zone is in the label, not a footnote. Someone picking a
                    time is deciding at that moment what the digits mean, and an
                    answer further down the page arrives after the decision. */}
                <label htmlFor="when">
                  Schedule for later (optional)
                  <span className="muted"> — times in {c.zoneName}</span>
                </label>
                <input id="when" type="datetime-local" value={c.scheduledAt}
                       disabled={!c.canSchedule}
                       max={c.horizonInputMax}
                       onChange={(e) => c.setScheduledAt(e.target.value)} />
                {!c.canSchedule ? (
                  <p className="notice upgrade" style={{ marginTop: ".4rem" }}>
                    Scheduling is a paid feature. Your posts publish immediately.{" "}
                    <Link to="/plans">See plans</Link>
                  </p>
                ) : c.horizon ? (
                  <p className="muted" style={{ marginTop: ".4rem" }}>
                    Your plan schedules up to{" "}
                    {formatLimit(limitOf(entitlements, "scheduling_horizon_days"), "days")}{" "}
                    ahead — until {formatInZone(c.horizon, c.zone, { dateStyle: "medium" })}.
                  </p>
                ) : null}
                {c.scheduledAt && c.selectedPlatforms.includes("youtube") && (
                  <p className="muted" style={{ marginTop: ".4rem" }}>
                    Scheduled YouTube uploads stream from storage rather than from this browser,
                    which is slower and less reliable for large files.
                  </p>
                )}
                {c.items[0]?.asset && !c.scheduledAt && c.selectedPlatforms.includes("youtube") && (
                  <p className="muted" style={{ marginTop: ".4rem" }}>
                    This video is already in storage, so YouTube receives it from there rather than
                    from this browser. It publishes in the background — you can leave this page.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="card compose-tip">
            <span className="compose-tip-icon" aria-hidden="true">💡</span>
            <div>
              <strong>Pro tip</strong>
              <p className="muted">
                Keep the lines you write again and again in your{" "}
                <Link to="/captions">caption library</Link>, and put the booking link and phone
                number in <Link to="/captions">brand details</Link> on the same screen — templates
                fill them in for you, per brand, and they stay right when they change.
              </p>
            </div>
          </div>
        </aside>
      </div>

      {/* ------------------------------------------------- the deciding bar */}
      <div className="compose-actionbar">
        <div className="compose-actionbar-status" role="status">
          {c.busy ? (
            <span className="muted loading-row">
              <Spinner />
              {c.stage === "uploading-media" && (c.items.length > 1
                ? `Uploading media — ${Math.min(c.uploaded + 1, c.items.length)} of ${c.items.length}…`
                : "Uploading media…")}
              {c.stage === "creating" && "Creating post…"}
              {c.stage === "uploading-youtube" && `Uploading to YouTube — ${c.progress}%`}
              {c.stage === "dispatching" && "Publishing…"}
            </span>
          ) : !c.facts ? (
            <span className="muted">Add a photo or a video to publish this post.</span>
          ) : c.selected.size === 0 ? (
            <span className="muted">Choose at least one account under “Publish to”.</span>
          ) : c.blockedByRole ? (
            <span className="muted">
              Your role can prepare posts but not {c.scheduledAt ? "schedule" : "publish"} them —
              ask a manager or publisher in your workspace.
            </span>
          ) : c.postsLeft === 0 ? (
            <span className="muted">
              You have used every post in your plan this month.{" "}
              <Link to="/plans">See plans</Link>
            </span>
          ) : needsYouTubeTitle ? (
            <span className="muted">
              YouTube needs a title — it is under{" "}
              <button type="button" className="linkish" onClick={() => setSideTab("settings")}>
                Settings
              </button>.
            </span>
          ) : !c.allReady && !c.checking ? (
            <span className="muted">Resolve the errors above before publishing.</span>
          ) : c.postsLeft !== null && c.postsLeft > 0 && c.postsLeft <= 3 ? (
            <span className="muted">{c.postsLeft} posts left this month on your plan.</span>
          ) : (
            <span className="muted">Ready to go out.</span>
          )}
          {c.stage === "uploading-youtube" && (
            <div className="bar"><div style={{ width: `${c.progress}%` }} /></div>
          )}
        </div>

        {/* The same `scheduledAt` the Settings panel writes — one value, two
            places to reach it, so there is no version of this screen where the
            bar says "Publish now" while the panel holds a date. Here because
            "now or later" is part of pressing the button, not a setting you go
            and find. */}
        <label className={c.scheduledAt ? "compose-schedule set" : "compose-schedule"}>
          <span className="compose-schedule-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 10h18M8 3v4M16 3v4" />
            </svg>
          </span>
          <span className="visually-hidden">Schedule for later (optional)</span>
          <input type="datetime-local" value={c.scheduledAt}
                 disabled={!c.canSchedule || c.busy}
                 max={c.horizonInputMax}
                 title={c.canSchedule
                   ? `Times are in ${c.zoneName}. Leave empty to publish straight away.`
                   : "Scheduling is a paid feature — see plans"}
                 onChange={(e) => c.setScheduledAt(e.target.value)} />
          {c.scheduledAt && (
            <button type="button" className="compose-schedule-clear" disabled={c.busy}
                    aria-label="Clear the schedule and publish straight away"
                    onClick={() => c.setScheduledAt("")}>×</button>
          )}
        </label>

        <div className="compose-actionbar-buttons">
          <button type="button" className={c.savingDraft ? "is-busy" : undefined}
                  onClick={() => void c.saveAsDraft()}
                  disabled={c.busy || c.savingDraft || !c.caption.trim()}>
            {c.savingDraft && <Spinner />}
            Save as draft
          </button>
          <button className={c.busy ? "primary is-busy" : "primary"}
                  onClick={() => void c.publish()} disabled={!canPublish}>
            {c.busy && <Spinner />}
            {c.scheduledAt ? "Schedule" : "Publish now"}
          </button>
        </div>
      </div>

      {pickingDraft && c.workspaceId && (
        <DraftPicker
          workspaceId={c.workspaceId}
          kind="caption"
          onPick={(d) => {
            c.replaceCaption(d.body, "draft");
            setWriterTab("write");
            setPickingDraft(false);
          }}
          onClose={() => setPickingDraft(false)}
        />
      )}

      {picking && c.workspaceId && (
        <MediaPicker
          workspaceId={c.workspaceId}
          brands={c.brands}
          initialBrandId={c.activeBrandId}
          // Once a kind is settled, offering the other one only leads to a
          // rejection: YouTube takes video, and swapping kinds mid-post silently
          // invalidates the targets already chosen.
          kind={c.selected.size > 0 ? c.kind : null}
          // What is left in the post, not what a carousel holds: a picker that
          // let somebody choose eight more photos for a post with four in it
          // would be collecting four rejections.
          max={Math.max(c.slotsLeft, 1)}
          onPick={(assets, urls) => { setPicking(false); c.useAssets(assets, urls); }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}
